import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import SiteLayout from "../components/SiteLayout";
import SEO from "../components/SEO";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import FileUploader from "../components/FileUploader";
import DatePicker from "../components/DatePicker";
import MarylandCoverage from "../components/MarylandCoverage";
import IntakeFormDialog from "../components/IntakeFormDialog";
import { CalendarDays, Clock, CheckCircle2, Mail, Phone, MapPin, Loader2, Video, FileSignature } from "lucide-react";
import { format } from "date-fns";

const SERVICES = [
 "Psychiatric Evaluation",
 "Medication Management",
 "Treatment Planning",
 "Follow-up Visit",
 "Telehealth Consultation",
 "Other",
];

const PROVIDERS = [
 "Aetna", "Blue Cross Blue Shield", "Cigna", "United Healthcare",
 "Medicare", "Medicaid", "Kaiser Permanente", "Humana", "Tricare", "Self-pay", "Other",
];

const SERVICE_FEES = {
 "Psychiatric Evaluation": 180,
 "Medication Management": 95,
 "Treatment Planning": 95,
 "Follow-up Visit": 95,
 "Telehealth Consultation": 95,
 "Other": 95,
};

function formatTime(t) {
 // "13:30" -> "1:30 PM"
 const [h, m] = t.split(":").map(Number);
 const ampm = h >= 12 ? "PM" : "AM";
 const hh = ((h + 11) % 12) + 1;
 return `${hh}:${m.toString().padStart(2, "0")} ${ampm}`;
}

function BookingFlow({ prefill }) {
 const today = new Date();
 const [date, setDate] = useState(undefined);
 const [slots, setSlots] = useState([]);
 const [slotsLoading, setSlotsLoading] = useState(false);
 const [closed, setClosed] = useState(false);
 const [time, setTime] = useState("");
 const [form, setForm] = useState({
 full_name: "",
 email: "",
 phone: "",
 service: (prefill?.service && SERVICES.includes(prefill.service)) ? prefill.service : SERVICES[0],
 notes: prefill?.notes || "",
 insurance: { provider: "", member_id: "", group_number: "", dob: "" },
 });
 const [idDocs, setIdDocs] = useState([]);
 const [insuranceDocs, setInsuranceDocs] = useState([]);
 const [otherDocs, setOtherDocs] = useState([]);
 const [submitting, setSubmitting] = useState(false);
 const [success, setSuccess] = useState(null);
 const [intakeOpen, setIntakeOpen] = useState(false);
 // "insurance" = bill insurance (no immediate charge, straight to consent).
 // "self_pay" = collect fee via Stripe now, then consent on success page.
 const [paymentMethod, setPaymentMethod] = useState("insurance");

 useEffect(() => {
 if (!date) { setSlots([]); setTime(""); setClosed(false); return; }
 const d = format(date, "yyyy-MM-dd");
 setSlotsLoading(true);
 api.get(`/availability?date=${d}`)
 .then((r) => { setSlots(r.data.available || []); setClosed(!!r.data.closed); setTime(""); })
 .catch((err) => {
 // Surface the underlying failure rather than silently masking it as
 // "all slots booked" (which made earlier CORS/network issues look like
 // a bookings problem). Defaults to closed so the user sees a clear
 // message, and logs full diagnostic info for support.
 console.error("Availability fetch failed", {
 date: d,
 status: err?.response?.status,
 detail: err?.response?.data?.detail,
 message: err?.message,
 });
 setSlots([]);
 setClosed(false);
 const networkErr = !err?.response;
 toast.error(networkErr
 ? "Couldn't load times. Check your connection and try again."
 : (formatApiError(err.response?.data?.detail) || "Couldn't load times for this date."));
 })
 .finally(() => setSlotsLoading(false));
 }, [date]);

 const minDate = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
 const maxDate = useMemo(() => { const d = new Date(); d.setMonth(d.getMonth() + 6); return d; }, []);

 async function submit(e) {
 e.preventDefault();
 if (!date) return toast.error("Please choose a date.");
 if (!time) return toast.error("Please choose a time slot.");
 if (!form.full_name || !form.email || !form.phone) return toast.error("Please complete name, email, and phone.");
 setSubmitting(true);
 try {
 const documents = [
 ...idDocs.map((d) => ({ id: d.id, original_name: d.original_name, kind: "id", content_type: d.content_type })),
 ...insuranceDocs.map((d) => ({ id: d.id, original_name: d.original_name, kind: "insurance", content_type: d.content_type })),
 ...otherDocs.map((d) => ({ id: d.id, original_name: d.original_name, kind: "other", content_type: d.content_type })),
 ];
 const baseBody = {
 full_name: form.full_name,
 email: form.email,
 phone: form.phone,
 service: form.service,
 notes: form.notes,
 insurance: form.insurance,
 documents,
 date: format(date, "yyyy-MM-dd"),
 time,
 care_match_session_id: prefill?.session_id || null,
 };

 if (paymentMethod === "self_pay") {
 // Self-pay: Stripe checkout → /payment/success → /consent (mandatory).
 const { data } = await api.post("/appointments/checkout", { ...baseBody, origin_url: window.location.origin });
 try {
 sessionStorage.setItem("gls_pending_booking", JSON.stringify({
 appointment_id: data.appointment_id,
 session_id: data.session_id,
 full_name: form.full_name,
 service: form.service,
 date: format(date, "yyyy-MM-dd"),
 time,
 amount: data.amount,
 currency: data.currency,
 }));
 } catch { /* sessionStorage may be blocked; success page falls back to API */ }
 window.location.assign(data.checkout_url);
 return;
 }

 // Insurance path: create appointment directly, then send patient to /consent
 // (mandatory legal step). No Stripe.
 const { data: appt } = await api.post("/appointments", baseBody);
 window.location.assign(`/consent?appointment_id=${appt.id}`);
 return;
 } catch (err) {
 toast.error(formatApiError(err.response?.data?.detail) || "Could not book. Try another time.");
 } finally {
 setSubmitting(false);
 }
 }

 if (success) {
 return (
 <>
 <div data-testid="booking-success" className="card-soft p-10 text-center">
 <div className="w-14 h-14 mx-auto rounded-2xl bg-sage-500/15 text-sage-700 flex items-center justify-center">
 <CheckCircle2 className="w-7 h-7" strokeWidth={1.75} />
 </div>
 <h3 className="mt-5 font-heading text-2xl font-semibold text-ink">Your request is in.</h3>
 <p className="mt-3 text-muted_text leading-relaxed max-w-md mx-auto">
 Thank you, {success.full_name.split(" ")[0]}. Your <span className="font-medium text-ink">{success.service}</span> for{" "}
 <span className="font-medium text-ink">{format(new Date(success.date), "EEEE, MMMM d")}</span> at{" "}
 <span className="font-medium text-ink">{formatTime(success.time)}</span> has been received. A care coordinator will reach out within one business day to confirm.
 </p>

 <div data-testid="booking-intake-notice" className="mt-8 mx-auto max-w-xl rounded-2xl border border-gold-500/30 bg-gold-50 px-5 py-5 text-left">
 <p className="font-heading text-base font-semibold text-ink inline-flex items-center gap-2">
 <FileSignature className="w-4 h-4 text-gold-700" strokeWidth={1.75} /> One more step
 </p>
 <p className="mt-2 text-sm text-ink/80 leading-relaxed">
 Please complete your <button
 type="button"
 data-testid="open-intake-link"
 onClick={() => setIntakeOpen(true)}
 className="font-semibold text-sage-800 hover:text-gold-700 underline underline-offset-4 decoration-gold-500 decoration-2"
 >intake, assessment &amp; consent form</button> ahead of your appointment. A specialist will contact you after submission.
 </p>
 </div>

 <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
 <button
 type="button"
 data-testid="open-intake-btn"
 onClick={() => setIntakeOpen(true)}
 className="btn-gold !text-sm"
 >
 Start intake form
 </button>
 <button
 onClick={() => { setSuccess(null); setDate(undefined); setTime(""); setIdDocs([]); setInsuranceDocs([]); setOtherDocs([]); setForm({ full_name: "", email: "", phone: "", service: SERVICES[0], notes: "", insurance: { provider: "", member_id: "", group_number: "", dob: "" } }); }}
 data-testid="booking-reset-btn"
 className="btn-ghost"
 >
 Book another appointment
 </button>
 </div>
 </div>

 <IntakeFormDialog open={intakeOpen} onOpenChange={setIntakeOpen} appointment={success} />
 </>
 );
 }

 return (
 <form onSubmit={submit} data-testid="booking-form" className="card-soft p-6 sm:p-8 lg:p-10 space-y-10">
 {/* Step 1: Date */}
 <div>
 <div className="flex items-center gap-2 mb-4">
 <span className="w-7 h-7 rounded-full bg-sage-700 text-white text-xs font-semibold inline-flex items-center justify-center">1</span>
 <p className="font-heading text-lg font-semibold text-ink">Choose a date</p>
 </div>
 <p className="text-sm text-muted_text mb-5">Available Monday through Saturday, 8:00 AM, 8:00 PM ET. Times shown in your local time.</p>
 <div className="rounded-2xl border border-[#E8EBE4] bg-cream p-3 sm:p-5 inline-block">
 <Calendar
 mode="single"
 selected={date}
 onSelect={setDate}
 disabled={(d) => d < minDate || d > maxDate || d.getDay() === 0}
 data-testid="booking-calendar"
 />
 </div>
 </div>

 {/* Step 2: Time */}
 <div>
 <div className="flex items-center gap-2 mb-4">
 <span className="w-7 h-7 rounded-full bg-sage-700 text-white text-xs font-semibold inline-flex items-center justify-center">2</span>
 <p className="font-heading text-lg font-semibold text-ink">Choose a time</p>
 </div>
 {!date && <p className="text-sm text-muted_text">Pick a date above to see available slots.</p>}
 {date && slotsLoading && (
 <p className="text-sm text-muted_text inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading slots...</p>
 )}
 {date && !slotsLoading && closed && (
 <p data-testid="slots-closed" className="text-sm text-muted_text">We're closed on this date, please pick another.</p>
 )}
 {date && !slotsLoading && !closed && slots.length === 0 && (
 <p data-testid="slots-empty" className="text-sm text-muted_text">All slots are booked for this date, please choose another day.</p>
 )}
 {date && !slotsLoading && slots.length > 0 && (
 <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
 {slots.map((s) => (
 <button
 type="button"
 key={s}
 onClick={() => setTime(s)}
 data-testid={`slot-${s}`}
 className={`rounded-xl px-3 py-3 text-sm font-medium border transition-all ${
 time === s
 ? "bg-sage-700 text-white border-sage-700 shadow-[0_8px_20px_rgba(91,133,170,0.25)]"
 : "bg-paper text-ink border-[#E2E8F0] hover:border-sage-400 hover:bg-sage-50"
 }`}
 >
 {formatTime(s)}
 </button>
 ))}
 </div>
 )}
 </div>

 {/* Step 3: Details */}
 <div>
 <div className="flex items-center gap-2 mb-4">
 <span className="w-7 h-7 rounded-full bg-sage-700 text-white text-xs font-semibold inline-flex items-center justify-center">3</span>
 <p className="font-heading text-lg font-semibold text-ink">Your details</p>
 </div>
 <div className="grid sm:grid-cols-2 gap-5">
 <div>
 <Label htmlFor="full_name">Full name</Label>
 <Input id="full_name" data-testid="booking-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Jane Doe" className="h-12 mt-2" required />
 </div>
 <div>
 <Label htmlFor="email">Email</Label>
 <Input id="email" type="email" data-testid="booking-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="h-12 mt-2" required />
 </div>
 <div>
 <Label htmlFor="phone">Phone</Label>
 <Input id="phone" data-testid="booking-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" className="h-12 mt-2" required />
 </div>
 <div>
 <Label>Service</Label>
 <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
 <SelectTrigger className="h-12 mt-2" data-testid="booking-service-trigger"><SelectValue /></SelectTrigger>
 <SelectContent>
 {SERVICES.map((s) => <SelectItem key={s} value={s} data-testid={`service-opt-${s}`}>{s}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 <div className="sm:col-span-2">
 <div className="rounded-2xl bg-sage-50/60 border border-sage-200/50 px-4 py-3 inline-flex items-center gap-2 text-sm text-sage-800" data-testid="booking-telehealth-note">
 <Video className="w-4 h-4" strokeWidth={1.75} /> All visits are conducted via secure telehealth video.
 </div>
 </div>
 <div className="sm:col-span-2">
 <Label htmlFor="notes">What would you like us to know? <span className="text-muted_text font-normal">(optional)</span></Label>
 <Textarea id="notes" data-testid="booking-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Briefly share what's on your mind, current treatments, or any preferences." className="mt-2 min-h-[120px]" />
 </div>
 </div>
 </div>

 {/* Step 4: Insurance & verification */}
 <div>
 <div className="flex items-center gap-2 mb-4">
 <span className="w-7 h-7 rounded-full bg-sage-700 text-white text-xs font-semibold inline-flex items-center justify-center">4</span>
 <p className="font-heading text-lg font-semibold text-ink">Insurance &amp; verification documents</p>
 </div>
 <p className="text-sm text-muted_text mb-5">Optional but recommended, uploading these now helps us verify your benefits before your first visit. All files are encrypted and visible only to our care team.</p>

 <div className="grid sm:grid-cols-2 gap-5">
 <div>
 <Label>Insurance provider</Label>
 <select
 data-testid="booking-ins-provider"
 value={form.insurance.provider}
 onChange={(e) => setForm({ ...form, insurance: { ...form.insurance, provider: e.target.value } })}
 className="h-12 mt-2 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
 >
 <option value="">Select your provider (optional)</option>
 {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
 </select>
 </div>
 <div>
 <Label>Member ID</Label>
 <Input data-testid="booking-ins-member" value={form.insurance.member_id} onChange={(e) => setForm({ ...form, insurance: { ...form.insurance, member_id: e.target.value } })} className="h-12 mt-2" placeholder="As shown on your card" />
 </div>
 <div>
 <Label>Group number <span className="text-muted_text font-normal">(optional)</span></Label>
 <Input data-testid="booking-ins-group" value={form.insurance.group_number} onChange={(e) => setForm({ ...form, insurance: { ...form.insurance, group_number: e.target.value } })} className="h-12 mt-2" />
 </div>
 <div>
 <Label>Date of birth <span className="text-muted_text font-normal">(optional)</span></Label>
 <div className="mt-2">
 <DatePicker
 testId="booking-ins-dob"
 value={form.insurance.dob}
 onChange={(v) => setForm({ ...form, insurance: { ...form.insurance, dob: v } })}
 placeholder="Select date of birth"
 maxDate={new Date()}
 />
 </div>
 </div>
 </div>

 <div className="mt-7 grid md:grid-cols-3 gap-5">
 <FileUploader
 testId="upload-id"
 kind="id"
 label="Government-issued ID"
 hint="Front of driver's licence, passport, or state ID."
 multiple={false}
 value={idDocs}
 onChange={setIdDocs}
 />
 <FileUploader
 testId="upload-insurance"
 kind="insurance"
 label="Insurance / Medicaid card"
 hint="Front and back if possible."
 multiple
 value={insuranceDocs}
 onChange={setInsuranceDocs}
 />
 <FileUploader
 testId="upload-other"
 kind="other"
 label="Other documents"
 hint="Lab results, prior records, medication list, etc."
 multiple
 value={otherDocs}
 onChange={setOtherDocs}
 />
 </div>
 </div>

 {/* Step 5: Payment method */}
 <div className="pt-6 border-t border-[#E8EBE4]">
 <div className="flex items-center gap-3 mb-3">
 <span className="w-7 h-7 rounded-full bg-sage-700 text-cream text-sm font-heading font-semibold flex items-center justify-center">5</span>
 <p className="font-heading text-lg font-semibold text-ink">How would you like to pay?</p>
 </div>
 <div className="grid sm:grid-cols-2 gap-3" data-testid="booking-payment-method">
 <label className={`cursor-pointer rounded-2xl border-2 p-4 transition ${paymentMethod === "insurance" ? "border-sage-700 bg-sage-500/5" : "border-[#E8EBE4] hover:border-sage-600/40"}`}>
 <input
 type="radio"
 name="pay_method"
 value="insurance"
 data-testid="booking-pay-insurance"
 checked={paymentMethod === "insurance"}
 onChange={() => setPaymentMethod("insurance")}
 className="sr-only"
 />
 <p className="font-heading text-base font-semibold text-ink">Bill my insurance</p>
 <p className="mt-1 text-xs text-muted_text leading-relaxed">
 We&apos;ll verify your benefits and bill your plan. No charge today. You&apos;ll be asked to sign the required consent form next.
 </p>
 </label>
 <label className={`cursor-pointer rounded-2xl border-2 p-4 transition ${paymentMethod === "self_pay" ? "border-gold-500 bg-gold-500/10" : "border-[#E8EBE4] hover:border-gold-500/50"}`}>
 <input
 type="radio"
 name="pay_method"
 value="self_pay"
 data-testid="booking-pay-selfpay"
 checked={paymentMethod === "self_pay"}
 onChange={() => setPaymentMethod("self_pay")}
 className="sr-only"
 />
 <p className="font-heading text-base font-semibold text-ink">Pay out-of-pocket now</p>
 <p className="mt-1 text-xs text-muted_text leading-relaxed">
 Charged securely via Stripe. Refundable up to 24 h before your visit. Consent form comes next.
 </p>
 </label>
 </div>
 </div>

 <div className="pt-4 border-t border-[#E8EBE4] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
 <div className="text-xs text-muted_text max-w-md space-y-1">
 {paymentMethod === "self_pay" ? (
 <p data-testid="booking-fee-hint" className="text-ink font-medium">
 Visit fee: <span className="text-sage-700">${SERVICE_FEES[form.service] ?? 95}</span>
 <span className="ml-1 text-muted_text font-normal">(secured via Stripe, refundable up to 24 h before visit)</span>
 </p>
 ) : (
 <p data-testid="booking-fee-hint" className="text-ink font-medium">
 No charge today <span className="text-muted_text font-normal">— your insurance will be billed. A required consent form will follow.</span>
 </p>
 )}
 <p>By booking, you agree to be contacted by our care team. We never share your information.</p>
 </div>
 <Button type="submit" data-testid="booking-submit" className="btn-gold !rounded-full !text-base !h-12 !px-8 !text-ink !font-semibold disabled:opacity-60" disabled={submitting}>
 {submitting
 ? (<><Loader2 className="w-4 h-4 animate-spin" /> {paymentMethod === "self_pay" ? "Redirecting to secure payment..." : "Confirming booking..."}</>)
 : (paymentMethod === "self_pay"
 ? (<>Pay ${SERVICE_FEES[form.service] ?? 95} &amp; continue</>)
 : (<>Confirm &amp; continue to consent</>))}
 </Button>
 </div>
 </form>
 );
}

function ContactForm() {
 const [form, setForm] = useState({ full_name: "", email: "", phone: "", subject: "", message: "" });
 const [sending, setSending] = useState(false);
 const [done, setDone] = useState(false);

 async function submit(e) {
 e.preventDefault();
 setSending(true);
 try {
 await api.post("/contact", form);
 setDone(true);
 toast.success("Message sent. We'll respond within one business day.");
 setForm({ full_name: "", email: "", phone: "", subject: "", message: "" });
 } catch (err) {
 toast.error(formatApiError(err.response?.data?.detail) || "Could not send your message.");
 } finally {
 setSending(false);
 }
 }

 return (
 <form onSubmit={submit} data-testid="contact-form" className="card-soft p-6 sm:p-8 lg:p-10 space-y-5">
 <div className="grid sm:grid-cols-2 gap-5">
 <div>
 <Label htmlFor="c_name">Full name</Label>
 <Input id="c_name" data-testid="contact-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="h-12 mt-2" required />
 </div>
 <div>
 <Label htmlFor="c_email">Email</Label>
 <Input id="c_email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 mt-2" required />
 </div>
 <div>
 <Label htmlFor="c_phone">Phone <span className="text-muted_text font-normal">(optional)</span></Label>
 <Input id="c_phone" data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 mt-2" />
 </div>
 <div>
 <Label htmlFor="c_subject">Subject</Label>
 <Input id="c_subject" data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-12 mt-2" />
 </div>
 </div>
 <div>
 <Label htmlFor="c_msg">Message</Label>
 <Textarea id="c_msg" data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 min-h-[160px]" required />
 </div>
 <div className="flex justify-end">
 <Button type="submit" data-testid="contact-submit" disabled={sending} className="btn-primary !rounded-full !h-12 !px-8 !text-base disabled:opacity-60">
 {sending ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>) : "Send message"}
 </Button>
 </div>
 {done && <p data-testid="contact-success" className="text-sm text-sage-700">Thanks, your message is in. We'll respond within one business day.</p>}
 </form>
 );
}

export default function Contact() {
 const location = useLocation();
 const prefill = location?.state?.prefill || null;

 return (
 <SiteLayout>
 <SEO
 title="Book a Telehealth Appointment"
 description="Reserve your virtual psychiatry visit in under 2 minutes. Same-week openings, secure document upload, and insurance or self-pay options at booking."
 path="/contact"
 />
 <Toaster richColors position="top-right" />
 <section data-testid="contact-hero" className="container-pad max-w-7xl mx-auto pt-14 lg:pt-20 pb-12">
 <div className="grid lg:grid-cols-12 gap-12 items-center">
 <div className="lg:col-span-7">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Contact & booking</p>
 <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink text-balance">
 Begin care today, <span className="italic text-sage-600">at your pace.</span>
 </h1>
 <p className="mt-6 text-lg text-muted_text leading-relaxed max-w-2xl">
 {prefill?.source === "care-match"
 ? "We've used your Care Match answers to pre-fill the booking. Pick a date and time that work for you, adjust anything else along the way."
 : "Choose the option that feels right. Book a confidential appointment online, or send a message and we'll reach out within one business day."}
 </p>
 {prefill?.source === "care-match" && (
 <div data-testid="prefill-banner" className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/15 text-gold-700 text-xs font-semibold tracking-[0.16em] uppercase">
 Care Match · suggestion applied
 </div>
 )}
 </div>
 <div className="lg:col-span-5">
 <div className="relative">
 <img
 src="https://images.pexels.com/photos/8867432/pexels-photo-8867432.jpeg?auto=compress&cs=tinysrgb&w=1200"
 alt="A friendly care coordinator ready to help with bookings and questions"
 className="rounded-3xl w-full h-[420px] object-cover shadow-[0_30px_60px_-30px_rgba(26,43,51,0.3)]"
 loading="lazy"
 />
 <div className="absolute -top-6 -left-6 w-32 h-32 rounded-3xl bg-gold-500/15 -z-10" />
 <div className="absolute -bottom-8 -right-6 w-40 h-40 rounded-3xl bg-sage-500/15 -z-10" />
 </div>
 </div>
 </div>
 </section>

 <section className="container-pad max-w-7xl mx-auto pb-24">
 <div className="grid lg:grid-cols-12 gap-10">
 <div className="lg:col-span-8">
 <Tabs defaultValue="book" className="w-full">
 <TabsList data-testid="contact-tabs" className="bg-paper border border-[#E8EBE4] rounded-full p-1 h-12">
 <TabsTrigger value="book" data-testid="tab-book" className="rounded-full px-6 h-10 data-[state=active]:bg-sage-700 data-[state=active]:text-white">
 <CalendarDays className="w-4 h-4 mr-2" strokeWidth={1.75} /> Book appointment
 </TabsTrigger>
 <TabsTrigger value="message" data-testid="tab-message" className="rounded-full px-6 h-10 data-[state=active]:bg-sage-700 data-[state=active]:text-white">
 <Mail className="w-4 h-4 mr-2" strokeWidth={1.75} /> Send a message
 </TabsTrigger>
 </TabsList>
 <TabsContent value="book" className="mt-6"><BookingFlow prefill={prefill} /></TabsContent>
 <TabsContent value="message" className="mt-6"><ContactForm /></TabsContent>
 </Tabs>
 </div>

 <aside data-testid="contact-aside" className="lg:col-span-4 space-y-5">
 <div className="card-soft p-7">
 <p className="font-heading text-lg font-semibold text-ink">Reach the team</p>
 <ul className="mt-5 space-y-4 text-sm">
 <li className="flex items-start gap-3 text-ink"><Mail className="w-4 h-4 mt-0.5 text-sage-700" strokeWidth={1.75} /> care@goldenlifesh.com</li>
 <li className="flex items-start gap-3 text-ink"><Phone className="w-4 h-4 mt-0.5 text-sage-700" strokeWidth={1.75} /> (443) 891-4248</li>
 <li className="flex items-start gap-3 text-ink"><Clock className="w-4 h-4 mt-0.5 text-sage-700" strokeWidth={1.75} /> Mon, Sat, 8:00 AM, 8:00 PM ET</li>
 </ul>
 </div>
 <div className="card-soft p-7">
 <MarylandCoverage />
 </div>
 <div className="rounded-3xl bg-ink text-cream p-7">
 <p className="font-heading text-lg font-semibold">In a crisis?</p>
 <p className="mt-3 text-sm text-cream/80 leading-relaxed">
 If you or a loved one are in immediate danger, please call 988 (Suicide & Crisis Lifeline) or 911. This site does not provide emergency services.
 </p>
 </div>
 </aside>
 </div>
 </section>
 </SiteLayout>
 );
}
